import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import { API } from '../../../../api';
import type { SharePointConnectionStatus } from '../../../../types/sharepoint';
import SharePointConnectModal from '../../../../components/integrations/SharePointConnectModal';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import type { TeamsConnectionStatus } from '../../../../types/teams';
import TeamsConnectModal from '../../../../components/integrations/TeamsConnectModal';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

/* ─────────────────────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────────────────────── */
const sectionCardSx = {
  alignSelf: 'stretch',
  padding: '20px',
  background: '#151515',
  borderRadius: '8px',
  flexDirection: 'column' as const,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: '16px',
  display: 'flex',
  [MOBILE_SX]: { padding: '14px', gap: '12px' }
};

const tileRowBaseSx = {
  alignSelf: 'stretch',
  padding: '16px',
  background: '#1C1C1C',
  borderRadius: '6px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '16px',
  display: 'inline-flex' as const,
  // A phone: the logo and the text share the first row; the action drops under the text.
  [MOBILE_SX]: {
    flexWrap: 'wrap' as const,
    padding: '12px',
    gap: '12px',
    // Sibling selectors off the logo, not :nth-child (emotion flags it as unsafe; the QA guard fails on that warning).
    '& > .integration-logo + *': { flex: '1 1 calc(100% - 52px)', minWidth: 0 },
    '& > .integration-logo + * ~ *': { ml: '52px' }
  }
};

const sectionLabelSx = {
  color: '#868686',
  fontSize: 12,
  fontFamily: 'Inter',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  wordWrap: 'break-word' as const
};

/* ─────────────────────────────────────────────────────────────
   Shared primitives
───────────────────────────────────────────────────────────── */

/** 40 × 40 coloured logo box with two-letter initials */
const LogoBox = ({ bg, initials }: { bg: string; initials: string }) => (
  <Box
    className="integration-logo"
    sx={{
      width: 40,
      height: 40,
      background: bg,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}
  >
    <Typography sx={{ color: '#fff', fontSize: 16, fontFamily: 'Inter', fontWeight: 700 }}>
      {initials}
    </Typography>
  </Box>
);

/** Orange-gradient CTA "Connect" button */
const CtaConnect = ({ onClick, disabled }: { onClick?: () => void; disabled?: boolean }) => (
  <Box
    component="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      paddingTop: '10px',
      paddingBottom: '10px',
      paddingLeft: '12px',
      paddingRight: '14px',
      // Dark with dim text while disabled, orange once it can be clicked.
      background: disabled ? 'rgba(0, 0, 0, 0.2)' : 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
      boxShadow: disabled ? 'inset 0 0 0 1px #2A2A2A' : 'none',
      borderRadius: '6px',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      cursor: disabled ? 'default' : 'pointer',
      flexShrink: 0,
      transition: 'opacity 0.15s',
      '&:hover': { opacity: disabled ? 1 : 0.9 }
    }}
  >
    <Typography
      sx={{
        color: disabled ? '#565656' : '#FDFDFD',
        fontSize: 13,
        fontFamily: 'Inter',
        fontWeight: 600,
        lineHeight: '19.5px'
      }}
    >
      Connect
    </Typography>
  </Box>
);

/** Grey "Coming Soon" pill */
const ComingSoonChip = () => (
  <Box
    sx={{
      paddingLeft: '12px',
      paddingRight: '12px',
      paddingTop: '6px',
      paddingBottom: '6px',
      background: '#2A2A2A',
      borderRadius: '6px',
      border: '1px solid #444444',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}
  >
    <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter', fontWeight: 500 }}>
      Coming Soon
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   SharePoint tile — manages its own connection state
───────────────────────────────────────────────────────────── */
function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
}

function truncate(url: string, max = 42): string {
  return url.length <= max ? url : `${url.slice(0, max)}…`;
}

const SharePointTile = ({ orgId }: { orgId: string }) => {
  const [status, setStatus] = useState<SharePointConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const data = await API.getSharePointConnection(orgId);
      setStatus(data);
    } catch {
      setStatus({ isConnected: false, status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try { await API.triggerSharePointSync(orgId); await fetchStatus(); } catch { /* silent */ }
    finally { setSyncing(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect SharePoint? Synced documents will remain in Knowledge.')) return;
    setDisconnecting(true);
    try { await API.deleteSharePointConnection(orgId); await fetchStatus(); } catch { /* silent */ }
    finally { setDisconnecting(false); }
  };

  const handleConnected = async () => {
    setLoading(true);
    await fetchStatus();
  };

  /* Loading */
  if (loading) {
    return (
      <Box sx={tileRowBaseSx}>
        <LogoBox bg="#0078D7" initials="SP" />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
            SharePoint
          </Typography>
          <Bone width={220} height={14} sx={{ mt: 0.75 }} />
        </Box>
        <Bone width={96} height={34} />
      </Box>
    );
  }

  const isConnected = status?.isConnected === true;

  /* Disconnected */
  if (!isConnected) {
    return (
      <>
        <Box sx={tileRowBaseSx}>
          <LogoBox bg="#0078D7" initials="SP" />
          <Box sx={{ flex: '1 1 0', flexDirection: 'column', display: 'flex', gap: '4px' }}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
              SharePoint
            </Typography>
          </Box>
          <CtaConnect onClick={() => setModalOpen(true)} />
        </Box>
        <SharePointConnectModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConnected={handleConnected}
          orgId={orgId}
        />
      </>
    );
  }

  /* Connected */
  return (
    <Box sx={tileRowBaseSx}>
      <LogoBox bg="#0078D7" initials="SP" />

      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
            SharePoint
          </Typography>
          <Box
            sx={{
              px: '8px',
              py: '2px',
              background: 'rgba(76, 175, 80, 0.12)',
              borderRadius: '20px'
            }}
          >
            <Typography sx={{ color: '#4CAF50', fontSize: 11, fontFamily: 'Inter', fontWeight: 600, lineHeight: '16px' }}>
              {status?.status === 'syncing' ? 'Syncing' : 'Connected'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', mt: '3px' }}>
          {status?.siteUrl && (
            <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px' }}>
              {truncate(status.siteUrl)}
            </Typography>
          )}
          {status?.lastSyncAt && (
            <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px' }}>
              Last synced: {formatRelativeTime(status.lastSyncAt)}
            </Typography>
          )}
          {status?.documentCount !== undefined && (
            <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px' }}>
              {status.documentCount} document{status.documentCount !== 1 ? 's' : ''} synced
            </Typography>
          )}
        </Box>

        {status?.errorMessage && (
          <Typography sx={{ color: '#F44336', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px', mt: '2px' }}>
            {status.errorMessage}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <Box
          component="button"
          onClick={handleSync}
          disabled={syncing || status?.status === 'syncing'}
          sx={{
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '14px',
            paddingRight: '14px',
            background: syncing || status?.status === 'syncing' ? 'rgba(0, 0, 0, 0.2)' : '#1C1C1C',
            borderRadius: '6px',
            border: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: syncing ? 'default' : 'pointer'
          }}
        >
          <Typography
            sx={{
              color: syncing || status?.status === 'syncing' ? '#565656' : '#FDFDFD',
              fontSize: 13,
              fontFamily: 'Inter',
              fontWeight: 500,
              lineHeight: '19.5px'
            }}
          >
            {syncing || status?.status === 'syncing' ? 'Syncing...' : 'Sync Now'}
          </Typography>
        </Box>

        <Typography
          component="button"
          onClick={handleDisconnect}
          disabled={disconnecting}
          sx={{
            background: 'none',
            border: 'none',
            cursor: disconnecting ? 'default' : 'pointer',
            color: disconnecting ? '#565656' : '#868686',
            fontSize: 12,
            fontFamily: 'Inter',
            fontWeight: 500,
            lineHeight: '18px',
            p: 0,
            '&:hover': { color: disconnecting ? '#565656' : '#F44336' },
            transition: 'color 0.15s'
          }}
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Typography>
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   Microsoft Teams tile — org-level notification channel (Option C)
───────────────────────────────────────────────────────────── */
const TeamsTile = ({ orgId }: { orgId: string }) => {
  const [status, setStatus] = useState<TeamsConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setStatus(await API.getTeamsConnection(orgId));
    } catch {
      setStatus({ status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleConnected = async () => {
    setLoading(true);
    await fetchStatus();
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Microsoft Teams? People will receive notifications by email again.')) return;
    setWorking(true);
    try { await API.disconnectTeams(orgId); await fetchStatus(); } catch { /* silent */ }
    finally { setWorking(false); }
  };

  if (loading) {
    return (
      <Box sx={tileRowBaseSx}>
        <LogoBox bg="#6264A7" initials="MT" />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
            Microsoft Teams
          </Typography>
          <Bone width={220} height={14} sx={{ mt: 0.75 }} />
        </Box>
        <Bone width={96} height={34} />
      </Box>
    );
  }

  const isConnected = status?.status === 'connected';

  if (!isConnected) {
    const isError = status?.status === 'error';
    return (
      <>
        <Box sx={tileRowBaseSx}>
          <LogoBox bg="#6264A7" initials="MT" />
          <Box sx={{ flex: '1 1 0', flexDirection: 'column', display: 'flex', gap: '4px' }}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
              Microsoft Teams
            </Typography>
            <Typography sx={{ color: isError ? '#F44336' : '#868686', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px' }}>
              {isError
                ? status?.errorMessage || 'Connection error. Reconnect to retry.'
                : 'Deliver notifications in Teams for your people on Microsoft 365.'}
            </Typography>
          </Box>
          <CtaConnect onClick={() => setModalOpen(true)} />
        </Box>
        <TeamsConnectModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConnected={handleConnected}
          orgId={orgId}
        />
      </>
    );
  }

  return (
    <Box sx={tileRowBaseSx}>
      <LogoBox bg="#6264A7" initials="MT" />
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
            Microsoft Teams
          </Typography>
          <Box sx={{ px: '8px', py: '2px', background: 'rgba(76, 175, 80, 0.12)', borderRadius: '20px' }}>
            <Typography sx={{ color: '#4CAF50', fontSize: 11, fontFamily: 'Inter', fontWeight: 600, lineHeight: '16px' }}>
              Connected
            </Typography>
          </Box>
        </Box>
        {status?.matchedUserCount !== undefined && status?.totalUserCount !== undefined && (
          <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px', mt: '3px' }}>
            {status.matchedUserCount} of {status.totalUserCount} people matched to Microsoft 365
          </Typography>
        )}
        {status?.errorMessage && (
          <Typography sx={{ color: '#F44336', fontSize: 12, fontFamily: 'Inter', lineHeight: '18px', mt: '2px' }}>
            {status.errorMessage}
          </Typography>
        )}
      </Box>
      <Typography
        component="button"
        onClick={handleDisconnect}
        disabled={working}
        sx={{
          background: 'none',
          border: 'none',
          cursor: working ? 'default' : 'pointer',
          color: working ? '#565656' : '#868686',
          fontSize: 12,
          fontFamily: 'Inter',
          fontWeight: 500,
          lineHeight: '18px',
          p: 0,
          flexShrink: 0,
          '&:hover': { color: working ? '#565656' : '#F44336' },
          transition: 'color 0.15s'
        }}
      >
        {working ? 'Disconnecting...' : 'Disconnect'}
      </Typography>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────── */
const IntegrationsPage = () => {
  const { orgId } = useProgramContext();

  return (
    <Box
      sx={{
        alignSelf: 'stretch',
        padding: '25px',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '32px',
        display: 'flex',
        // A phone: the app shell already pads the page.
        [MOBILE_SX]: { padding: 0 }
      }}
    >
      <Box
        sx={{
          alignSelf: 'stretch',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: '24px',
          display: 'flex',
          [MOBILE_SX]: { gap: '12px' }
        }}
      >

        {/* ── Knowledge Base ──────────────────────────────────── */}
        <Box sx={sectionCardSx}>
          <Typography sx={sectionLabelSx}>Knowledge Base</Typography>
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: '#8E8E8E',
              fontSize: 13,
              fontFamily: 'Inter',
              fontWeight: 400,
              lineHeight: '18.20px'
            }}
          >
            Connect your content sources so your Knowledge Base stays current automatically, giving Elliott and your
            admins accurate, up-to-date material to build learning from.
          </Typography>
          <SharePointTile orgId={orgId} />
        </Box>

        {/* ── Workspace ───────────────────────────────────────── */}
        <Box sx={sectionCardSx}>
          <Typography sx={sectionLabelSx}>Workspace</Typography>
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: '#8E8E8E',
              fontSize: 13,
              fontFamily: 'Inter',
              fontWeight: 400,
              lineHeight: '18.20px'
            }}
          >
            Deliver learning notifications and updates directly in the tools your team already uses every day, so nothing gets missed and nothing requires a new habit.
          </Typography>
          <TeamsTile orgId={orgId} />
        </Box>

        {/* ── HRIS ────────────────────────────────────────────── */}
        <Box sx={sectionCardSx}>
          <Typography sx={sectionLabelSx}>HRIS</Typography>
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: '#B6B6B6',
              fontSize: 12,
              fontFamily: 'Inter',
              fontWeight: 400
            }}
          >
            Connect your HR system so Dosen always knows who&apos;s joined, changed role, or moved team, and can act on
            it automatically, without any manual intervention.
          </Typography>
          <Box sx={{ ...tileRowBaseSx, opacity: 0.60 }}>
            <LogoBox bg="#F79420" initials="HB" />
            <Box sx={{ flex: '1 1 0', flexDirection: 'column', display: 'flex', gap: '4px' }}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontFamily: 'Inter', fontWeight: 600 }}>
                HiBob
              </Typography>
            </Box>
            <ComingSoonChip />
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default IntegrationsPage;
